// hieroglyphReading.ts — parses the free-text `reading` field found on Kama
// (Egyptian hieroglyph) entries to surface two things the prose already
// contains but that no UI currently exploits: the Gardiner sign codes of a
// word's spelling, and citations of other entries ("voir la fiche « X » dans Y").

import { CategoryDictionary, WordData } from '../types/types';

// Gardiner's Sign List (1927) groups every hieroglyph under a letter
// category. This classification is standard Egyptological reference data,
// not specific to any one sign — used here to give a code like "G22" a
// human-readable category without asserting a precise (unverified) meaning
// for that individual sign.
const GARDINER_CATEGORY_LABELS: Record<string, string> = {
  A: 'Homme et ses activités',
  B: 'Femme et ses activités',
  C: 'Divinités anthropomorphes',
  D: 'Parties du corps humain',
  E: 'Mammifères',
  F: 'Parties de mammifères',
  G: 'Oiseaux',
  H: 'Parties d\'oiseaux',
  I: 'Amphibiens, reptiles',
  K: 'Poissons et parties de poissons',
  L: 'Invertébrés et animaux divers',
  M: 'Arbres et plantes',
  N: 'Ciel, terre, eau',
  O: 'Bâtiments, parties de bâtiments',
  P: 'Navires et parties de navires',
  Q: 'Mobilier domestique et funéraire',
  R: 'Mobilier de temple, emblèmes sacrés',
  S: 'Couronnes, vêtements, bâtons',
  T: 'Guerre, chasse, boucherie',
  U: 'Agriculture, artisanat, métiers',
  V: 'Corde, fibres, paniers, sacs',
  W: 'Récipients de pierre et de terre',
  X: 'Pains et gâteaux',
  Y: 'Écriture, jeux, musique',
  Z: 'Traits, figures géométriques',
  AA: 'Signes non classés',
};

const GARDINER_CATEGORY_LABELS_EN: Record<string, string> = {
  A: 'Man and his occupations',
  B: 'Woman and her occupations',
  C: 'Anthropomorphic deities',
  D: 'Parts of the human body',
  E: 'Mammals',
  F: 'Parts of mammals',
  G: 'Birds',
  H: 'Parts of birds',
  I: 'Amphibious animals, reptiles',
  K: 'Fish and parts of fish',
  L: 'Invertebrates and lesser animals',
  M: 'Trees and plants',
  N: 'Sky, earth, water',
  O: 'Buildings, parts of buildings',
  P: 'Ships and parts of ships',
  Q: 'Domestic and funerary furniture',
  R: 'Temple furniture and sacred emblems',
  S: 'Crowns, dress, staves',
  T: 'Warfare, hunting, butchery',
  U: 'Agriculture, crafts, trades',
  V: 'Rope, fiber, baskets, bags',
  W: 'Vessels of stone and earthenware',
  X: 'Loaves and cakes',
  Y: 'Writing, games, music',
  Z: 'Strokes, geometrical figures',
  AA: 'Unclassified signs',
};

export interface GardinerSign {
  code: string; // e.g. "G22" or "Aa1"
  categoryLetter: string; // e.g. "G" or "AA"
  categoryLabel: string; // e.g. "Oiseaux"
}

// Trailing letter accepts both cases: lowercase for sub-variants written
// lowercase in prose (e.g. "D21a"), uppercase for the Unicode-standard
// extended-variant suffix (e.g. "N35A", "G7A").
const SIGN_TOKEN = '[A-Z](?:[a-z])?\\d{1,3}[A-Za-z]?';
const CHAIN_RE = new RegExp(`\\b(${SIGN_TOKEN}(?:-${SIGN_TOKEN}){1,7})\\b`);
const TOKEN_RE = new RegExp(SIGN_TOKEN, 'g');

function labelForCode(code: string, language: string): { categoryLetter: string; categoryLabel: string } {
  const letters = code.match(/^[A-Z][a-z]?/)?.[0] ?? '';
  const categoryLetter = letters.length === 2 ? letters.toUpperCase() : letters;
  const labels = language === 'en' ? GARDINER_CATEGORY_LABELS_EN : GARDINER_CATEGORY_LABELS;
  const fallback = language === 'en' ? 'Uncategorized sign' : 'Catégorie non répertoriée';
  const categoryLabel = labels[categoryLetter] ?? fallback;
  return { categoryLetter, categoryLabel };
}

/**
 * Extracts the first hyphen-chain of Gardiner codes found in a `reading`
 * text (e.g. "ḏbt (G22-D58-X1), « brique »" → ["G22", "D58", "X1"]).
 * Returns an empty array if no chain of 2+ codes is present. `language`
 * selects which Gardiner category-label dictionary to use (defaults to
 * French, since `reading` prose itself always stays French).
 */
export function extractGardinerCodes(reading?: string, language: string = 'fr'): GardinerSign[] {
  if (!reading) return [];
  const match = reading.match(CHAIN_RE);
  if (!match) return [];
  const codes = match[1].match(TOKEN_RE) ?? [];
  return codes.map((code) => ({ code, ...labelForCode(code, language) }));
}

export interface CrossReference {
  citedKey: string; // transliteration as written in the citing text, e.g. "jꜥrt"
  citedCategoryRaw: string; // category name as written, e.g. "Objets"
  resolvedCategoryKey: string | null; // matching key in CategoryDictionary, if found
  word: WordData | null; // resolved entry, if found
}

const XREF_RE = /voir la fiche\s*«\s*([^»]+?)\s*»\s*dans\s+([A-ZÀ-Þ][A-Za-zÀ-ÿ]*)/g;

function resolveCategoryKey(raw: string, categories: CategoryDictionary): string | null {
  const needle = raw.toLowerCase();
  const found = Object.keys(categories).find((key) => key.toLowerCase().startsWith(needle));
  return found ?? null;
}

/**
 * Finds every "voir la fiche « X » dans Y" citation in a `reading` text and
 * resolves it against the real category data, so the citation becomes a
 * usable link rather than inert prose.
 */
export function extractCrossReferences(
  reading: string | undefined,
  categories: CategoryDictionary,
): CrossReference[] {
  if (!reading) return [];
  const results: CrossReference[] = [];
  const seen = new Set<string>();

  for (const match of reading.matchAll(XREF_RE)) {
    const citedKey = match[1].trim();
    const citedCategoryRaw = match[2].trim();
    const dedupeKey = `${citedKey}::${citedCategoryRaw}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const resolvedCategoryKey = resolveCategoryKey(citedCategoryRaw, categories);
    // Kama categories are always flat CategoryData (word → WordData), never
    // GrammarCategory, so this cast is safe for this dictionary.
    const categoryData = resolvedCategoryKey
      ? (categories[resolvedCategoryKey] as Record<string, WordData>)
      : null;
    const word = categoryData?.[citedKey] ?? null;

    results.push({ citedKey, citedCategoryRaw, resolvedCategoryKey, word });
  }

  return results;
}

/**
 * Resolves a sign's `signExample` (see WordData) into the same shape as a
 * cross-reference, so the example word can reuse the cross-reference detour
 * (KamaLearnMode's navigateToReference) when tapped.
 */
export function resolveSignExample(
  example: WordData['signExample'],
  categories: CategoryDictionary,
): (CrossReference & { word: WordData }) | null {
  if (!example) return null;
  const categoryData = categories[example.category] as Record<string, WordData> | undefined;
  const word = categoryData?.[example.key];
  if (!word) return null;
  return { citedKey: example.key, citedCategoryRaw: example.category, resolvedCategoryKey: example.category, word };
}

export interface TextSegment {
  text: string;
  coptic: boolean;
}

// Coptic script spans two disjoint Unicode ranges: the dedicated Coptic
// block (U+2C80-U+2CFF) and a handful of Bohairic-only letters (Shei, Fei,
// Khei, Hori, Gangia, Shima, Dei — e.g. ϥ, ϧ) left in the legacy Greek block
// at U+03E2-U+03EF for backward compatibility, never duplicated into the
// newer block. General-purpose serif/system fonts on Windows were verified
// (fontTools cmap inspection) to cover the first range but not the second,
// silently substituting the wrong glyph for just those few letters even
// mid-word — hence isolating every Coptic run so it can be given its own
// font, rather than trusting the surrounding paragraph's font stack.
const COPTIC_RUN_RE = /[Ϣ-ϯⲀ-⳿]+/g;

/**
 * Splits `text` into alternating Coptic / non-Coptic runs, so the caller can
 * render Coptic substrings (often embedded mid-sentence in French prose,
 * e.g. "copte ⲛⲟⲩϥⲉ, « profit »") with a font guaranteed to cover them.
 */
export function splitCopticRuns(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let lastIndex = 0;
  COPTIC_RUN_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = COPTIC_RUN_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), coptic: false });
    }
    segments.push({ text: match[0], coptic: true });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), coptic: false });
  }
  return segments;
}
